import { LabelShowingResults, Typography } from '@/components/common';
import { Input } from '@/components/form';
import Icons from '@/const/icons';
import { useFilters } from '@/hooks';
import * as React from 'react';
import { useForm } from 'react-hook-form';

interface ClientDirectoryHeaderProps {
	title: string;
	showingItems: number;
	totalItems: number;
	labelTotalItems: string;
	allowDynamicUpdate?: boolean; // Nuevo prop opcional
}

export const ClientDirectoryHeader: React.FC<ClientDirectoryHeaderProps> = ({
	title,
	showingItems,
	totalItems,
	labelTotalItems,
	allowDynamicUpdate = false, // Por defecto false para mantener comportamiento existente
}) => {
	const { register } = useForm();
	const { searchFilter, params } = useFilters();
	const totalRecords = React.useRef(null);

	React.useEffect(() => {
		if (allowDynamicUpdate) {
			// Si se permite actualización dinámica, siempre actualizar
			if (totalItems > 0) {
				(totalRecords.current as number | null) = totalItems;
			}
		} else {
			// Comportamiento original: solo se setea una vez
			if (totalRecords.current === null && totalItems > 0) {
				(totalRecords.current as number | null) = totalItems;
			}
		}
	}, [totalItems, allowDynamicUpdate]);

	// Calculate which total to show depending on dynamic update flag
	const displayedTotal = allowDynamicUpdate
		? totalItems // always reflect latest total from API
		: totalRecords.current || totalItems; // preserve original behaviour

	return (
		<div className="flex justify-between items-center w-full">
			<div className="flex items-center gap-x-3">
				<Typography type="semibold-20" className="font-semibold">
					{title}
				</Typography>
				<Typography type="regular-14" className="text-opacity-black">
					{displayedTotal} clients
				</Typography>
			</div>
			<div className="flex items-center gap-x-2">
				<Input
					name="search"
					register={register}
					placeholder="Search"
					iconLeft={Icons.search}
					classNameIcon={'!top-[23%] !w-4 !h-4 text-opacity-black'}
					classNameInput="pl-9 xl:w-72 !bg-gray-15 !h-[33px] !m-0 border-none 3xl:!text-sm"
					onChange={({ target }) => searchFilter(target.value)}
				/>
				<LabelShowingResults
					total={showingItems}
					totalItems={totalItems}
					labelTotalItems={labelTotalItems}
					isSearched={params.size > 1}
					allowDynamicUpdate={allowDynamicUpdate}
				/>
			</div>
		</div>
	);
};
